import {useState, useEffect, useCallback, useMemo} from "react"
import {
    Box, Card, CardContent, FormControl, Grid, InputLabel,
    MenuItem, Select, TextField, Typography, Button,
    CircularProgress, Alert,
} from "@mui/material"
import {
    Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
    Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip,
    XAxis, YAxis,
} from "recharts"
import {useTranslation} from "react-i18next"
import RefreshIcon from "@mui/icons-material/Refresh"
import BarChartIcon from "@mui/icons-material/BarChart"
import axiosInstance from "../../config/axios-config"
import i18n from "i18next"

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82CA9D", "#FFC658", "#FF7300", "#00C49F", "#FF00FF"]

const getDateMinusDays = (days) => {
    const d = new Date()
    d.setDate(d.getDate() - days)
    return d.toISOString().split("T")[0]
}

const TabAnalytics = ({tabValue, tenantId}) => {
    const {t} = useTranslation()
    const [dateFrom, setDateFrom] = useState(getDateMinusDays(30))
    const [dateTo, setDateTo] = useState(getDateMinusDays(0))
    const [chartType, setChartType] = useState("bar")
    const [limit, setLimit] = useState(10)
    const [productData, setProductData] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const fetchData = useCallback(async () => {
        if (!tenantId) return
        setLoading(true)
        setError(null)
        try {
            const res = await axiosInstance.get("/api/v1/statics/best-selling-products", {
                params: {
                    startDate: new Date(dateFrom).toISOString(),
                    endDate: new Date(dateTo).toISOString(),
                    limit,
                },
                headers: {"Language-Context": (i18n.language || "en").toUpperCase()},
            })
            setProductData(res.data.map((p) => ({
                name: p.productName,
                orders: p.totalCount,
                revenue: p.totalRevenue,
                category: p.category,
            })))
        } catch (err) {
            setError(err.response?.data?.message || t("FailedToLoadAnalytics"))
        } finally {
            setLoading(false)
        }
    }, [tenantId, dateFrom, dateTo, limit])

    useEffect(() => {
        if (tabValue === 1) fetchData()
    }, [tabValue, fetchData, i18n.language])

    const categoryData = useMemo(() => {
        const map = new Map()
        productData.forEach((p) => {
            const key = p.category || "Other"
            map.set(key, (map.get(key) || 0) + p.orders)
        })
        return Array.from(map.entries()).map(([name, value], idx) => ({
            name: t(name), value, color: COLORS[idx % COLORS.length]
        }))
    }, [productData, t])

    const renderChart = () => {
        if (loading) return <Box display="flex" justifyContent="center" alignItems="center"
                                 height={400}><CircularProgress/></Box>
        if (!productData.length) return <Box display="flex" justifyContent="center" alignItems="center"
                                             height={400}><Typography
            color="textSecondary">{t("NoDataAvailable")}</Typography></Box>

        switch (chartType) {
            case "bar":
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={productData}>
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} fontSize={12}/>
                            <YAxis/>
                            <RechartsTooltip/>
                            <Legend/>
                            <Bar dataKey="orders" name={t("Orders")}>
                                {productData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]}/>)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )
            case "pie":
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <PieChart>
                            <Pie
                                data={productData} cx="50%" cy="50%"
                                labelLine={false}
                                label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                outerRadius={130} dataKey="orders"
                            >
                                {productData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]}/>)}
                            </Pie>
                            <RechartsTooltip/>
                            <Legend/>
                        </PieChart>
                    </ResponsiveContainer>
                )
            case "line":
                // Line chart shows revenue trend per product (top 3 for readability)
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={productData.slice(0, 10)}>
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} fontSize={11}/>
                            <YAxis/>
                            <RechartsTooltip formatter={(v) => [`${v.toLocaleString()}֏`, t("Revenue")]}/>
                            <Legend/>
                            <Bar dataKey="revenue" name={t("Revenue")}>
                                {productData.slice(0, 10).map((_, idx) => <Cell key={idx}
                                                                                fill={COLORS[idx % COLORS.length]}/>)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )
            default:
                return null
        }
    }

    return (
        <div role="tabpanel" hidden={tabValue !== 1} id="pharmacy-tabpanel-1" aria-labelledby="pharmacy-tab-1">
            {tabValue === 1 && (
                <Box>
                    {/* Header */}
                    <Box display="flex" flexWrap="wrap" gap={2} justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h6" sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <BarChartIcon color="primary"/> {t("OrderAnalytics")}
                        </Typography>
                        <Button variant="outlined" startIcon={<RefreshIcon/>} onClick={fetchData} disabled={loading}>
                            {t("Refresh")}
                        </Button>
                    </Box>

                    {error && <Alert severity="error" sx={{mb: 3}}>{error}</Alert>}

                    {/* Filters */}
                    <Card sx={{mb: 3}}>
                        <CardContent>
                            <Typography variant="subtitle1" fontWeight={600}
                                        gutterBottom>{t("AnalyticsFilters")}</Typography>
                            <Typography variant="body2" color="textSecondary"
                                        sx={{mb: 2}}>{t("ChartFilterDesc")}</Typography>
                            <Grid container spacing={3} >
                                <Grid item xs={12} md={3}>
                                    <TextField fullWidth label={t("FromDate")} type="date"
                                               value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                               InputLabelProps={{shrink: true}} inputProps={{max: dateTo}}/>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField fullWidth label={t("ToDate")} type="date"
                                               value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                               InputLabelProps={{shrink: true}}
                                               inputProps={{min: dateFrom, max: getDateMinusDays(0)}}/>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField fullWidth label={t("NumberOfProducts")} type="number"
                                               value={limit}
                                               onChange={(e) => setLimit(Math.max(1, Math.min(50, parseInt(e.target.value) || 10)))}
                                               InputProps={{inputProps: {min: 1, max: 50}}}
                                               helperText={t("Max50Products")}/>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>{t("ChartType")}</InputLabel>
                                        <Select value={chartType} label={t("ChartType")}
                                                onChange={(e) => setChartType(e.target.value)}>
                                            <MenuItem value="bar">{t("BarChart")}</MenuItem>
                                            <MenuItem value="pie">{t("PieChart")}</MenuItem>
                                            <MenuItem value="line">{t("RevenueBarChart")}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Box mt={2} display="flex" justifyContent="flex-end">
                                <Button variant="contained" onClick={fetchData} disabled={loading} sx={{minWidth: 120}}>
                                    {loading ? <CircularProgress size={24}/> : t("ApplyFilters")}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Main chart */}
                    <Card sx={{mb: 3}}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t("Top10MostOrderedMedications")} ({productData.length})
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{mb: 2}}>
                                {chartType === "bar" && t("BarChartDesc")}
                                {chartType === "pie" && t("PieChartDesc")}
                                {chartType === "line" && t("LineChartDesc")}
                            </Typography>
                            {renderChart()}
                        </CardContent>
                    </Card>

                    {/* Secondary charts */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>{t("Top10DistributorsByOrder")}</Typography>
                                    <Typography variant="body2" color="textSecondary"
                                                sx={{mb: 2}}>{t("Top10DistributorsByOrderDesc")}</Typography>
                                    {loading ? (
                                        <Box display="flex" justifyContent="center" py={5}><CircularProgress/></Box>
                                    ) : !productData.length ? (
                                        <Box display="flex" justifyContent="center" alignItems="center"
                                             height={250}><Typography
                                            color="textSecondary">{t("NoDataAvailable")}</Typography></Box>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={250}>
                                            <BarChart data={productData.slice(0, 5)} layout="horizontal">
                                                <CartesianGrid strokeDasharray="3 3"/>
                                                <XAxis type="number"/>
                                                <YAxis dataKey="name" type="category" width={110} fontSize={11}/>
                                                <RechartsTooltip
                                                    formatter={(v) => [`${v.toLocaleString()}֏`, t("Revenue")]}/>
                                                <Bar dataKey="revenue" fill="#1976d2"/>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>{t("OrdersByCategory")}</Typography>
                                    <Typography variant="body2" color="textSecondary"
                                                sx={{mb: 2}}>{t("OrdersByCategoryDesc")}</Typography>
                                    {loading ? (
                                        <Box display="flex" justifyContent="center" py={5}><CircularProgress/></Box>
                                    ) : !categoryData.length ? (
                                        <Box display="flex" justifyContent="center" alignItems="center"
                                             height={250}><Typography
                                            color="textSecondary">{t("NoDataAvailable")}</Typography></Box>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={250}>
                                            <PieChart>
                                                <Pie data={categoryData} cx="50%" cy="50%"
                                                     innerRadius={40} outerRadius={80}
                                                     paddingAngle={5} dataKey="value">
                                                    {categoryData.map((entry, idx) => <Cell key={idx}
                                                                                            fill={entry.color}/>)}
                                                </Pie>
                                                <RechartsTooltip/>
                                                <Legend/>
                                            </PieChart>
                                        </ResponsiveContainer>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </div>
    )
}

export default TabAnalytics
